import { Injectable, NgZone } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { ConfigService } from '../config/config.service';

declare global {
	interface Window {
		// gtag empuja `arguments` al dataLayer
		dataLayer: unknown[];
		gtag?: Gtag;
	}
}

type Gtag = (...args: unknown[]) => void;

@Injectable({
	providedIn: 'root'
})
export class GoogleAnalyticsService {
	private measurementId = '';
	private lastPath: string | null = null;
	private analyticsDebugMode = false;

	constructor(
		private readonly router: Router,
		private readonly title: Title,
		private readonly ngZone: NgZone,
		private readonly config: ConfigService
	) {}

	/**
	 * Carga gtag si `google_analytics_measurement_id` y `google_analytics_enabled` en app.config,
	 * y el host coincide con `url_dns_host` (misma imagen, distinto ConfigMap por ambiente).
	 */
	init(): void {
		const measurementId = this.config.googleAnalyticsMeasurementId;
		if (!measurementId) {
			return;
		}
		if (!this.config.googleAnalyticsEnabled) {
			return;
		}
		const configuredHost = GoogleAnalyticsService.canonicalHostname(this.config.urlDnsHost);
		const currentHost = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
		if (!configuredHost || configuredHost !== currentHost) {
			return;
		}
		this.measurementId = measurementId;
		this.analyticsDebugMode =
			typeof window !== 'undefined' && window.location.hostname.toLowerCase() === 'localhost';
		void this.bootstrap().catch(() => undefined);
	}

	/** Hostname sin puerto; admite `host`, `host:puerto` o URL con protocolo. */
	private static canonicalHostname(value: string): string {
		const raw = value.trim().toLowerCase();
		if (!raw) {
			return '';
		}
		let hostPart = raw;
		if (raw.includes('://')) {
			try {
				hostPart = new URL(raw).host;
			} catch {
				return '';
			}
		} else {
			hostPart = raw.split('/')[0] ?? raw;
		}
		const colonIdx = hostPart.lastIndexOf(':');
		if (colonIdx > 0) {
			const maybePort = hostPart.slice(colonIdx + 1);
			if (/^\d+$/.test(maybePort)) {
				return hostPart.slice(0, colonIdx);
			}
		}
		return hostPart;
	}

	private async bootstrap(): Promise<void> {
		window.dataLayer = window.dataLayer || [];
		// Stub antes del script: la cola se procesa cuando carga gtag.js. No reemplazar
		// `window.gtag` después del onload: el stub machacaría la implementación real y
		// los hits (p. ej. `/g/collect`) no se enviarían.
		if (!window.gtag) {
			window.gtag = function gtag() {
				window.dataLayer.push(arguments as unknown as unknown);
			} as Gtag;
		}
		await this.injectGtagScript(this.measurementId);
		window.gtag('js', new Date());
		const baseConfig: Record<string, boolean> = { send_page_view: false };
		if (this.analyticsDebugMode) {
			baseConfig.debug_mode = true;
		}
		window.gtag('config', this.measurementId, baseConfig);

		this.ngZone.runOutsideAngular(() => {
			this.router.events
				.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
				.subscribe((e) => this.sendPageView(e.urlAfterRedirects || e.url));
		});

		this.sendPageView(this.router.url);
	}

	private injectGtagScript(measurementId: string): Promise<void> {
		return new Promise((resolve, reject) => {
			if (document.querySelector('script[src*="googletagmanager.com/gtag/js"]')) {
				resolve();
				return;
			}
			const script = document.createElement('script');
			script.async = true;
			script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
			script.onload = () => resolve();
			script.onerror = () => reject(new Error('gtag.js failed to load'));
			document.head.appendChild(script);
		});
	}

	private sendPageView(path: string): void {
		if (!window.gtag) {
			return;
		}
		const normalized = path.startsWith('/') ? path : `/${path}`;
		if (this.lastPath === normalized) {
			return;
		}
		this.lastPath = normalized;
		// `page_view` explícito: un segundo `gtag('config', …)` no siempre genera otro hit útil;
		// sin `page_location`, GA4 usa `page_path` (con `useHash`, `location.pathname` suele ser `/`).
		const payload: Record<string, string | boolean> = {
			page_path: normalized,
			page_title: this.title.getTitle()
		};
		if (this.analyticsDebugMode) {
			payload.debug_mode = true;
		}
		window.gtag('event', 'page_view', payload);
	}
}
